import { useEffect, useState } from "react";
import { toast } from "sonner";
import { getVideos, putVideos, formatApiError } from "@/lib/api";
import { Loader2, RefreshCw, Save, Youtube } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { parseYouTubeId, ytEmbed, isMp4 } from "@/lib/video";

function VideoField({ label, description, value, onChange, testid }) {
    const id = parseYouTubeId(value);
    const validYt = !!id;
    const validMp4 = isMp4(value);
    return (
        <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm space-y-4">
            <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-red-50 text-red-600 flex items-center justify-center shrink-0">
                    <Youtube className="w-5 h-5" />
                </div>
                <div>
                    <h3 className="text-base font-semibold text-slate-900">{label}</h3>
                    <p className="text-xs text-slate-500">{description}</p>
                </div>
            </div>
            <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">YouTube URL</Label>
                <Input
                    data-testid={testid}
                    value={value ?? ""}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder="https://youtu.be/xxxxxxxxxxx or https://www.youtube.com/watch?v=..."
                    className="h-11 rounded-xl bg-white"
                />
                {value && !validYt && !validMp4 && (
                    <div className="text-xs text-red-600">Invalid URL — paste a YouTube link (youtu.be / youtube.com / shorts) or an MP4 URL.</div>
                )}
            </div>
            {validYt && (
                <div className="rounded-xl overflow-hidden border border-slate-200 aspect-video bg-black">
                    <iframe
                        key={value}
                        title={id}
                        src={ytEmbed(value, { autoplay: false, mute: true })}
                        className="w-full h-full"
                        allow="accelerometer; clipboard-write; encrypted-media; picture-in-picture"
                        allowFullScreen
                    />
                </div>
            )}
            {validMp4 && (
                <video key={value} src={value} controls muted className="w-full rounded-xl aspect-video bg-black" />
            )}
        </div>
    );
}

export default function VideosTab() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const load = async () => {
        setLoading(true);
        try { setData(await getVideos()); }
        catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const save = async () => {
        setSaving(true);
        try {
            const payload = { ...data, hero_autoplay: true, hero_muted: true };
            setData(await putVideos(payload));
            toast.success("Videos updated on live landing page ✅");
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };
    const set = (k) => (v) => setData({ ...data, [k]: v });

    if (loading || !data) {
        return (
            <div className="flex items-center justify-center py-20 text-slate-500">
                <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading…
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex flex-wrap items-center justify-between gap-3 sticky top-14 bg-slate-50 z-10 py-3 -mx-6 px-6 border-b border-slate-200">
                <div>
                    <h2 className="text-xl font-bold text-slate-900">Video Management</h2>
                    <p className="text-xs text-slate-500 mt-0.5">Landing page वरील सर्व videos एका क्लिकवर update करा — Save केल्यावर लगेच LIVE.</p>
                </div>
                <div className="flex gap-2">
                    <button onClick={load} className="inline-flex items-center gap-2 rounded-lg bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm px-3 py-2" data-testid="videos-reload">
                        <RefreshCw className="w-4 h-4" /> Reload
                    </button>
                    <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm px-4 py-2 disabled:opacity-70" data-testid="videos-save">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Changes
                    </button>
                </div>
            </div>

            <VideoField
                label="Hero / Welcome Video"
                description="Landing page च्या hero section मध्ये (right side / mobile वर सर्वात वर) दिसतो. Auto-play muted."
                value={data.hero_video_url}
                onChange={set("hero_video_url")}
                testid="v-hero-url"
            />
            <VideoField
                label="Mentor Video"
                description="'Meet Your Mentor' section मध्ये click-to-play video."
                value={data.mentor_video_url}
                onChange={set("mentor_video_url")}
                testid="v-mentor-url"
            />
            <VideoField
                label="Seminar Review / Closing Video"
                description="Page च्या शेवटी (Contact च्या नंतर) दिसणारा click-to-play video."
                value={data.closing_video_url}
                onChange={set("closing_video_url")}
                testid="v-closing-url"
            />
        </div>
    );
}
